import {Component, inject, signal, ChangeDetectionStrategy} from "@angular/core"
import {AbstractControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators} from "@angular/forms"
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterLink} from "@angular/router"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {AuthService} from "@common/services/auth/auth.service"
import {RegisterUserGQL, ResendVerificationEmailGQL} from "@pages/sign-up/sign-up-page.generated"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {extractErrorInfo} from "@app/common/helpers/api/errors"
import {environment} from "@environment"
import {Settings} from "@app/common/models/settings/settings"
import {v4 as uuid4} from "uuid"
import {NgOptimizedImage} from "@angular/common"
import {z} from "zod"

// Use Zod for email validation to match backend validation
const emailSchema = z.string().email()

function zodEmailValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null // Let required validator handle empty
    return emailSchema.safeParse(control.value).success ? null : {email: true}
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-sign-up-page",
    templateUrl: "sign-up-page.component.html",
    styleUrls: ["sign-up-page.component.scss"],
    imports: [ReactiveFormsModule, MatProgressSpinnerModule, MatTooltipModule, InputContainerComponent, RouterLink, NgOptimizedImage],
})
export class SignUpPageComponent {
    readonly $showProgressCircle = signal(false)
    readonly $registrationComplete = signal(false)
    readonly $emailForResend = signal<string | null>(null)
    readonly $resendError = signal<string | null>(null)
    readonly $resendDisabled = signal(false)
    readonly $registrationError = signal<string | null>(null)
    readonly $showLoginLink = signal(false)
    readonly $confirmPasswordError = signal<string | null>(null)
    // Set once the user first presses the submit button; validation errors stay hidden until then.
    readonly $submitAttempted = signal(false)
    private resendTimerId: ReturnType<typeof setTimeout> | null = null

    // SSO links for registration
    readonly $googleSsoLink = signal<string | undefined>(undefined)
    readonly $microsoftSsoLink = signal<string | undefined>(undefined)

    signUpForm = new UntypedFormGroup({
        emailFormControl: new UntypedFormControl("", [Validators.required, zodEmailValidator]),
        passwordFormControl: new UntypedFormControl("", [Validators.required, Validators.minLength(8)]),
        confirmPasswordFormControl: new UntypedFormControl("", [Validators.required]),
    })

    readonly notifications = inject(NotificationsService)
    readonly registerUserGql = inject(RegisterUserGQL)
    readonly resendVerificationEmailGql = inject(ResendVerificationEmailGQL)
    readonly auth = inject(AuthService)

    constructor() {
        this.$googleSsoLink.set(this.getGoogleSsoSignUpLink())
        this.$microsoftSsoLink.set(this.getMicrosoftSsoSignUpLink())
    }

    private getGoogleSsoSignUpLink(): string | undefined {
        const clientId = environment.auth.google.clientId
        if (clientId) {
            const queryParams = new URLSearchParams({
                client_id: clientId,
                redirect_uri: `${environment.platformDeployUrl}/sso/google/signup`,
                response_type: "code",
                scope: "openid email profile",
                state: btoa(JSON.stringify({csrfToken: this.auth.csrfToken, flow: "signup"})),
            })
            return `${Settings.GOOGLE_OAUTH_ENDPOINT}?${queryParams}`
        }
        return undefined
    }

    private getMicrosoftSsoSignUpLink(): string | undefined {
        const clientId = environment.auth.microsoft.clientId
        if (clientId) {
            const queryParams = new URLSearchParams({
                client_id: clientId,
                redirect_uri: `${environment.platformDeployUrl}/sso/microsoft/signup`,
                response_type: "code",
                scope: "openid profile email offline_access",
                state: btoa(JSON.stringify({csrfToken: this.auth.csrfToken, flow: "signup"})),
                response_mode: "fragment",
                nonce: uuid4(),
            })
            return `${Settings.MICROSOFT_OAUTH_ENDPOINT}?${queryParams}`
        }
        return undefined
    }

    validateConfirmPassword(): void {
        const password = this.signUpForm.value.passwordFormControl
        const confirmPassword = this.signUpForm.value.confirmPasswordFormControl
        if (confirmPassword && password !== confirmPassword) {
            this.$confirmPasswordError.set("Passwords do not match")
        } else {
            this.$confirmPasswordError.set(null)
        }
    }

    async onSignUp(): Promise<void> {
        this.$submitAttempted.set(true)
        // Button stays enabled; the errors surfaced here (invalid email, too-short password, mismatch) are the gate.
        this.validateConfirmPassword()
        if (this.$confirmPasswordError() || !this.signUpForm.valid) {
            return
        }

        this.$showProgressCircle.set(true)
        this.$registrationError.set(null)
        this.$showLoginLink.set(false)

        try {
            const result = await mutateThrowingErrors(this.registerUserGql)({
                email: this.signUpForm.value.emailFormControl,
                password: this.signUpForm.value.passwordFormControl,
            })

            if (result?.registerUser) {
                this.$registrationComplete.set(true)
                this.$emailForResend.set(result.registerUser.email)
            }
        } catch (error) {
            // Check if this is a conflict error (email already in use)
            const errorInfo = extractErrorInfo(error)
            if (errorInfo.code === 409) {
                this.$registrationError.set("This email address is already registered.")
                this.$showLoginLink.set(true)
            } else if (errorInfo.code === 429) {
                this.$registrationError.set(errorInfo.message)
            } else {
                throw error
            }
        } finally {
            this.$showProgressCircle.set(false)
        }
    }

    async onResendVerification(): Promise<void> {
        const email = this.$emailForResend()
        if (!email) return

        // Clear any existing error
        this.$resendError.set(null)
        this.$showProgressCircle.set(true)

        try {
            await mutateThrowingErrors(this.resendVerificationEmailGql)({email})
            this.notifications.showInfo("Verification email sent! Please check your inbox.")
        } catch (error) {
            const errorInfo = extractErrorInfo(error)
            if (errorInfo.code === 429) {
                // Display inline error
                this.$resendError.set(errorInfo.message)
                this.$resendDisabled.set(true)

                // Clear any existing timer
                if (this.resendTimerId) {
                    clearTimeout(this.resendTimerId)
                }

                // Set timer to re-enable button after 1 hour
                const ONE_HOUR_MS = 60 * 60 * 1000
                this.resendTimerId = setTimeout(() => {
                    this.$resendError.set(null)
                    this.$resendDisabled.set(false)
                    this.resendTimerId = null
                }, ONE_HOUR_MS)
            } else {
                // re-throw error
                throw error
            }
        } finally {
            this.$showProgressCircle.set(false)
        }
    }
}
